import { useEffect, useMemo, useState } from 'react'
import { useSettings } from 'core/hooks/settings/useSettings'
import useBulkInvite from 'core/hooks/members/useBulkInvite'
import useBulkMemberUpdate from 'core/hooks/members/useBulkMemberUpdate'
import useReportBulkUpdate from 'core/hooks/reports/useReportBulkUpdate'
import usePayPeriodAction from 'core/hooks/payperiods/usePayPeriodAction'
import usePayPeriodDownload from 'core/hooks/payperiods/usePayPeriodDownload'
import useMemberExport from 'core/hooks/members/useMemberExport'
import {
  matchPath,
  Outlet,
  useNavigation as useReactRouterNavigation
} from 'react-router'
import { PasswordResetUrl, SltLoginUrl } from 'constants/RouteUrls'
import useUnitScript from 'core/hooks/utils/useUnitScript'
import useDownloadPeriodSummary from 'core/hooks/payperiods/useDownloadPeriodSummary'
import useDownloadMergeManagers from 'core/hooks/integrations/merge/useDownloadMergeManagers'
import usePeriodsFundingRequest from 'core/hooks/payperiods/usePeriodsFundingRequest'
import useBrazeInit from 'core/hooks/analytics/useBrazeInit'
import useImportTeamPlaces from 'core/hooks/team/useImportTeamPlaces'
import useMergePublic from 'core/hooks/integrations/merge/useMergePublic'
import useUserAnalytics from 'core/hooks/analytics/useUserAnalytics'
import useNavigation from 'core/hooks/navigation/useNavigation'
import AppLoadingIndicator from 'core/widgets/app-loader/AppLoadingIndicator'
import TopNav from 'core/widgets/top-nav/TopNav'
import ApplyMobileView from 'app/ApplyMobileView'
import MobileWarning from 'app/MobileWarning'
import { Toaster } from 'react-hot-toast'
import FeatureFlagController from 'app/feature-flag/FeatureFlagController'
import ExpiredBankNotification from 'core/widgets/notifications/ExpiredBankNotification'
import MergeSuccessModal from 'core/widgets/merge-success-modal/MergeSuccessModal'
import RatingModal from 'core/widgets/rating-modal/RatingModal'
import TripDetailDrawer from 'core/widgets/trip-detail/TripDetailDrawer'
import TransactionDetailDrawer from 'core/widgets/transaction-detail/TransactionDetailDrawer'
import UnitMFA from 'core/widgets/unit-mfa/UnitMFA'
import useBatchInvite from 'core/hooks/members/useBatchInvite'
import useBatchDeleteInvite from 'core/hooks/members/useBatchDeleteInvite'
import useMergePreviewEmployees from 'core/hooks/integrations/merge/useMergePreviewEmployees'
import FundingModal from 'core/widgets/funding-modal/FundingModal'
import useAuth from 'core/hooks/auth/useAuth'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import useUserLocation from 'core/hooks/user/useUserLocation'
import useDisableAppsFlyer from 'core/hooks/analytics/useDisableAppsFlyer'
import useRefreshToken from 'core/hooks/auth/useRefreshToken'
import useInitDataDog from 'core/hooks/analytics/useInitDataDog'
import useTheme from 'core/hooks/ui/useTheme'
import useReportsAction from 'core/hooks/reports/useReportsAction'
import useSalesDemoSandbox from 'core/hooks/sales/useSalesDemoSandbox'
import { UrlParamsProvider } from 'core/contexts/urlParamsContext'
import useTaxReportJob from 'core/hooks/tax/useTaxReportJob'
import useFinChatbot from 'core/hooks/analytics/useFinChatbot'

/**
 * Root layout component that serves as the main entry point for the application.
 *
 * Initializes all global hooks, validates authentication, manages feature flags,
 * and renders global UI components (modals, drawers, notifications, navigation).
 *
 * @remarks
 * **Key Responsibilities:**
 * - Initializes authentication and validates user session
 * - Sets up automatic token refresh for Motus sessions
 * - Initializes batch operation hooks (invites, exports, bulk updates)
 * - Configures feature flags based on user/team settings
 * - Handles SLT (Secure Login Token) authentication flow
 * - Handles hash-based password reset redirects
 * - Renders global UI components (TopNav, modals, drawers, toasts)
 * - Shows loading indicator during authentication
 *
 * @returns The root layout with nested routes via `<Outlet />`
 */
const RouteLayout = () => {
  useRefreshToken()
  useDisableAppsFlyer()
  useUserAnalytics()
  useMergePublic()
  useBrazeInit()
  useUnitScript()
  useBulkInvite()
  useBatchInvite()
  useBatchDeleteInvite()
  useBulkMemberUpdate()
  useImportTeamPlaces()
  useReportBulkUpdate()
  useReportsAction()
  usePayPeriodAction()
  usePayPeriodDownload()
  useMemberExport()
  useDownloadPeriodSummary()
  useDownloadMergeManagers({})
  usePeriodsFundingRequest()
  useMergePreviewEmployees()
  useInitDataDog()
  useTheme()
  useSalesDemoSandbox()
  useTaxReportJob()
  useFinChatbot()

  const { data: settings, set } = useSettings()
  const { featureFlags } = settings
  const { ENABLE_PAYMENTS_M3 } = featureFlags
  const { isAuthLoading, isAuthenticated } = useAuth({ validateAuth: true })

  const { pathname, search, replace } = useNavigation()
  const reactRouterNavigation = useReactRouterNavigation()
  const [dirty, setDirty] = useState(false)
  const [isInitialMount, setIsInitialMount] = useState(true)
  const { getUserLocation } = useUserLocation()
  const slt = useMemo(() => new URLSearchParams(search).get('slt'), [search])

  // Handle initial mount loading state
  useEffect(() => {
    if (isInitialMount && !isAuthLoading) {
      setIsInitialMount(false)
    }
  }, [isAuthLoading, isInitialMount])

  /**
   * Fetch user profile and configure feature flags based on user/team settings.
   * Only runs once when authenticated (dirty flag prevents re-configuration).
   */
  useUserProfile({
    opts: {
      enabled: isAuthenticated,
      onSuccess: (user) => {
        // Configure feature flags based on team settings (runs once)
        if (!dirty) {
          const newFeatureFlags: Record<string, boolean> = {}
          if (user?.teams?.[0]?.enable_payments) {
            newFeatureFlags['ENABLE_EVERLANCE_PAYMENTS'] = true
          }
          if (user?.teams?.[0]?.no_program_enabled) {
            newFeatureFlags['ENABLE_NEXT_PROGRAM'] = true
          }
          if (Object.keys(newFeatureFlags).length > 0) {
            set('featureFlags', {
              ...featureFlags,
              ...newFeatureFlags
            })
            setDirty(true)
          }
        }

        getUserLocation()
      }
    }
  })

  /**
   * Handles hash-based password reset URL redirects.
   * Converts hash routes (e.g., #/password-reset) to regular routes.
   * */
  useEffect(() => {
    const hashPath = window.location.hash.slice(1)
    const match = matchPath(PasswordResetUrl, hashPath)
    if (match) {
      replace(match?.pathname)
    }
  }, [replace])

  // Show loading indicator during initial mount, authentication, or navigation
  if (
    isInitialMount ||
    isAuthLoading ||
    reactRouterNavigation.state === 'loading'
  ) {
    return <AppLoadingIndicator />
  }

  // Redirect to SLT login if user has SLT token but isn't authenticated
  if (!isAuthenticated && slt && !pathname.includes('slt')) {
    replace(`${SltLoginUrl}?slt=${slt}`)
  }

  return (
    <main>
      <UrlParamsProvider>
        <FeatureFlagController />
        <ExpiredBankNotification />
        <MergeSuccessModal />
        <RatingModal />
        {ENABLE_PAYMENTS_M3 && <FundingModal />}

        {/*  Trip Detail Drawer */}
        <TripDetailDrawer />

        {/*  Transaction Detail Drawer */}
        <TransactionDetailDrawer />

        {/* Unit Second Factor Authenticator */}
        <UnitMFA />
        <Toaster
          position="top-right"
          gutter={16}
          toastOptions={{
            duration: 3000
          }}
        />
        <TopNav />
        <ApplyMobileView />
        <MobileWarning />
        <Outlet />
      </UrlParamsProvider>
    </main>
  )
}
export default RouteLayout
